import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Lock, Pencil, Plus, Trash2, Wand2 } from "lucide-react";
import useAuth from "../../../../hooks/useAuth";
import { cn } from "../../../../utils/cn";
import { showToast } from "../../../../utils/showToast";
import {
  createMacro,
  deleteMacro,
  getMacros,
  updateMacro,
} from "../../../../services/helpdeskWorkflowService";
import { isEndpointMissing } from "../../../../services/aiChatService";
import useHelpdeskLabels from "../../../../components/helpdesk/useHelpdeskLabels";
import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/Input";
import { Modal } from "../../../../components/ui/Modal";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { FieldLabel } from "../../../../components/ui/FieldLabel";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import ActionStepsEditor, { withStepKey } from "./ActionStepsEditor";
import { useWorkflowCatalogs } from "./useWorkflowCatalogs";
import {
  ACTION_DEFS,
  MACRO_ACTION_TYPES,
  MACRO_NAME_MAX,
  MAX_ACTIONS,
  MAX_MACROS,
  idOf,
  isWorkflowSupervisor,
  normalizeMacroActions,
  serializeAction,
  validateAction,
} from "./workflowSchema";
import {
  InlineFormError,
  ListSkeleton,
  LoadErrorBox,
  RefreshButton,
  SectionHeader,
  toastMutationError,
  workflowErrorMessage,
} from "./workflowUi";

// Macros: secuencias de acciones que el agente ejecuta en un clic desde el chat
// (etiquetar, asignar, mover, anotar, responder, resolver). Las globales son del
// equipo y las edita supervisión; las personales, solo su dueño.

const EMPTY_DRAFT = { name: "", visibility: "personal", actions: [] };

/** ¿Puede este usuario editar/borrar la macro? */
export function canEditMacro(macro, user) {
  if (macro?.visibility === "global") return isWorkflowSupervisor(user);
  return !!user?.id && String(macro?.owner_id) === String(user.id);
}

/**
 * Validación pura del editor de macros.
 * @returns {{name?:string, actions?:string, steps:boolean}}  `steps` = algún paso incompleto
 */
export function validateMacroDraft(draft, t) {
  const errors = {};
  const name = (draft.name || "").trim();
  if (!name) errors.name = t("El nombre es obligatorio.");
  else if (name.length > MACRO_NAME_MAX) errors.name = t("Máximo 60 caracteres.");
  if (draft.actions.length === 0) errors.actions = t("Agrega al menos una acción.");
  else if (draft.actions.length > MAX_ACTIONS) errors.actions = t("Máximo 10 acciones.");
  else if (draft.actions.some((a) => validateAction(a, t))) {
    errors.actions = t("Completa las acciones marcadas.");
  }
  return errors;
}

function sortMacros(list) {
  return [...list].sort((a, b) => {
    const ap = Number.isFinite(a?.position) ? a.position : Number.MAX_SAFE_INTEGER;
    const bp = Number.isFinite(b?.position) ? b.position : Number.MAX_SAFE_INTEGER;
    if (ap !== bp) return ap - bp;
    return (a?.name || "").localeCompare(b?.name || "");
  });
}

function actionsSummary(actions, t) {
  return (actions || [])
    .map((a) => (ACTION_DEFS[a?.type] ? t(ACTION_DEFS[a.type].label) : a?.type))
    .join(" → ");
}

// ── Editor ───────────────────────────────────────────────────────────────────
function MacroFormModal({ open, mode, draft, onChange, onSubmit, onClose, saving, serverError, canGlobal }) {
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = useState(false);
  const { labels } = useHelpdeskLabels();
  const { data: catalogs, errors: catalogErrors, retry } = useWorkflowCatalogs(
    ["team", "folders", "quickReplies"],
    open,
  );
  const errors = useMemo(() => validateMacroDraft(draft, t), [draft, t]);

  useEffect(() => {
    if (open) setShowErrors(false);
  }, [open]);

  const handleSubmit = () => {
    setShowErrors(true);
    if (Object.keys(errors).length > 0) return;
    onSubmit();
  };

  const visibilityOptions = [
    {
      value: "personal",
      icon: Lock,
      title: t("Personal"),
      hint: t("Solo tú la ves y la usas."),
      disabled: false,
    },
    {
      value: "global",
      icon: Globe,
      title: t("Global"),
      hint: canGlobal
        ? t("Todo el equipo la ve y la puede usar.")
        : t("Solo supervisores y administradores crean macros globales."),
      disabled: !canGlobal,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={mode === "edit" ? t("Editar macro") : t("Nueva macro")}
      description={t(
        "Se ejecuta desde el chat en un clic, en este orden y como si lo hicieras tú. Si una acción falla, las siguientes no se ejecutan.",
      )}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button className="hd-brand-solid" onClick={handleSubmit} loading={saving}>
            {mode === "edit" ? t("Guardar cambios") : t("Crear macro")}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <InlineFormError id="macro-server-error">{serverError}</InlineFormError>

        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <FieldLabel className="hd-field-label" htmlFor="macro-name">
              {t("Nombre")}
            </FieldLabel>
            <span className="text-xs tabular-nums text-fg-subtle">
              {(draft.name || "").trim().length} / {MACRO_NAME_MAX}
            </span>
          </div>
          <Input
            id="macro-name"
            value={draft.name}
            maxLength={MACRO_NAME_MAX}
            invalid={showErrors && !!errors.name}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            placeholder={t("Ej: Reclamo recibido y derivado")}
          />
          {showErrors && errors.name && <p className="text-xs text-status-error">{errors.name}</p>}
        </div>

        <fieldset className="space-y-1.5">
          <legend className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider hd-field-label">
            {t("Quién la ve")}
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label={t("Quién la ve")}>
            {visibilityOptions.map((opt) => {
              const active = draft.visibility === opt.value;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={opt.disabled}
                  onClick={() => onChange({ ...draft, visibility: opt.value })}
                  className={cn(
                    "flex items-start gap-2.5 rounded-lg border p-3 text-left",
                    "transition-colors duration-150 ease-out motion-reduce:transition-none",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                    "disabled:opacity-60 disabled:cursor-not-allowed",
                    active ? "border-brand-500 bg-brand-50" : "border-border bg-bg hover:bg-bg-muted",
                  )}
                >
                  <Icon
                    className={cn("w-4 h-4 mt-0.5 shrink-0", active ? "text-brand-700" : "text-fg-muted")}
                    aria-hidden="true"
                  />
                  <span className="min-w-0">
                    <span className={cn("block text-sm font-medium", active ? "text-brand-700" : "text-fg")}>
                      {opt.title}
                    </span>
                    <span className="block text-xs text-fg-muted">{opt.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="space-y-2">
          <FieldLabel className="hd-field-label">{t("Acciones")}</FieldLabel>
          <ActionStepsEditor
            idPrefix="macro-step"
            mode="macro"
            actions={draft.actions}
            onChange={(actions) => onChange({ ...draft, actions })}
            allowedTypes={MACRO_ACTION_TYPES}
            labels={labels}
            catalogs={catalogs}
            catalogErrors={catalogErrors}
            onRetryCatalog={retry}
            showErrors={showErrors}
          />
          {showErrors && errors.actions && <p className="text-xs text-status-error">{errors.actions}</p>}
        </div>
      </div>
    </Modal>
  );
}

// ── Vista ────────────────────────────────────────────────────────────────────
export default function MacrosSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSupervisor = isWorkflowSupervisor(user);

  const [macros, setMacros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [backendMissing, setBackendMissing] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setBackendMissing(false);
    try {
      setMacros(sortMacros(await getMacros()));
    } catch (err) {
      if (isEndpointMissing(err)) {
        setBackendMissing(true);
        setMacros([]);
      } else {
        setLoadError(workflowErrorMessage(err, t("Revisa tu conexión o vuelve a intentarlo.")));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const atCap = macros.length >= MAX_MACROS;

  const openCreate = () => {
    setEditingId(null);
    setDraft({ ...EMPTY_DRAFT, actions: [] });
    setServerError(null);
    setFormOpen(true);
  };

  const openEdit = (macro) => {
    setEditingId(idOf(macro));
    setDraft({
      name: macro.name || "",
      visibility: macro.visibility === "global" ? "global" : "personal",
      actions: normalizeMacroActions(macro.actions).map(withStepKey),
    });
    setServerError(null);
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setServerError(null);
    const payload = {
      name: draft.name.trim(),
      visibility: draft.visibility,
      actions: normalizeMacroActions(draft.actions).map(serializeAction),
    };
    try {
      if (editingId) {
        const saved = await updateMacro(editingId, payload);
        setMacros((prev) =>
          sortMacros(prev.map((m) => (idOf(m) === editingId ? { ...m, ...payload, ...(saved || {}) } : m))),
        );
        showToast(t("Macro actualizada."), true);
      } else {
        const saved = await createMacro(payload);
        if (saved && idOf(saved)) setMacros((prev) => sortMacros([...prev, saved]));
        else await load();
        showToast(t("Macro creada."), true);
      }
      setFormOpen(false);
    } catch (err) {
      if (err?.status === 409) {
        setServerError(
          workflowErrorMessage(err, t("Ya existe una macro con ese nombre o se alcanzó el tope de 100 macros.")),
        );
      } else if (err?.status === 400 || err?.status === 403) {
        // Validación del backend (p. ej. una etiqueta que ya no existe) o
        // permiso: el texto del backend dice qué corregir y va junto al form.
        setServerError(workflowErrorMessage(err, t("No se pudo guardar la macro.")));
      } else {
        toastMutationError(err, t("No se pudo guardar la macro."), t);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteMacro(idOf(deleteTarget));
      setMacros((prev) => prev.filter((m) => idOf(m) !== idOf(deleteTarget)));
      showToast(t("Macro eliminada."), true);
      setDeleteTarget(null);
    } catch (err) {
      toastMutationError(err, t("No se pudo eliminar la macro."), t);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader
        title={t("Macros")}
        description={t(
          "Varias acciones en un clic desde el chat: etiquetar, asignar, mover a carpeta, dejar una nota, responder y resolver.",
        )}
        meta={
          !loading && !loadError
            ? t("{{count}} de {{max}} macros").replace("{{count}}", macros.length).replace("{{max}}", MAX_MACROS)
            : null
        }
        actions={
          <>
            <RefreshButton loading={loading} onClick={load} />
            <Button
              className="hd-brand-solid"
              iconLeft={<Plus className="w-4 h-4" aria-hidden="true" />}
              onClick={openCreate}
              disabled={atCap || loading}
              title={atCap ? t("Se alcanzó el tope de 100 macros.") : undefined}
            >
              {t("Nueva macro")}
            </Button>
          </>
        }
      />

      {backendMissing && (
        <PendingBackendNotice>
          {t("Las macros están en construcción. Los cambios no se guardarán hasta que estén disponibles.")}
        </PendingBackendNotice>
      )}

      {loading && <ListSkeleton rows={3} />}

      {!loading && loadError && (
        <LoadErrorBox title={t("No se pudieron cargar las macros.")} message={loadError} onRetry={load} />
      )}

      {!loading && !loadError && macros.length === 0 && (
        <div className="rounded-xl border border-border bg-bg">
          <EmptyState
            icon={<Wand2 aria-hidden="true" />}
            title={t("Todavía no hay macros")}
            description={t(
              "Una macro guarda lo que haces siempre igual —poner la etiqueta «Reclamo», asignarlo a bodega y avisar al cliente— para hacerlo en un clic.",
            )}
            action={
              !backendMissing ? (
                <Button
                  className="hd-brand-solid"
                  iconLeft={<Plus className="w-4 h-4" aria-hidden="true" />}
                  onClick={openCreate}
                >
                  {t("Crear la primera")}
                </Button>
              ) : null
            }
          />
        </div>
      )}

      {!loading && !loadError && macros.length > 0 && (
        <ul className="rounded-xl border border-border bg-bg divide-y divide-border" aria-label={t("Macros")}>
          {macros.map((macro) => {
            const editable = canEditMacro(macro, user);
            const isGlobal = macro.visibility === "global";
            const count = (macro.actions || []).length;
            return (
              <li key={idOf(macro)} className="flex items-center gap-3 px-[1rem] py-[0.75rem] sm:px-[1.5rem] sm:py-[1rem]">
                {/* Decorative tile: on a phone it took 36px from the name. */}
                <span className="max-sm:hidden inline-flex w-9 h-9 items-center justify-center rounded-lg bg-bg-muted text-fg-muted shrink-0">
                  <Wand2 className="w-4 h-4" aria-hidden="true" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-semibold text-fg truncate max-w-full max-md:basis-full max-md:whitespace-normal max-md:break-words">{macro.name}</span>
                    <Badge tone={isGlobal ? "brand" : "neutral"}>
                      {isGlobal ? (
                        <Globe className="w-3 h-3" aria-hidden="true" />
                      ) : (
                        <Lock className="w-3 h-3" aria-hidden="true" />
                      )}
                      {isGlobal ? t("Global") : t("Personal")}
                    </Badge>
                  </div>
                  <p className="mt-0.5 text-xs text-fg-muted truncate" title={actionsSummary(macro.actions, t)}>
                    {count === 1 ? t("1 acción") : t("{{count}} acciones").replace("{{count}}", count)}
                    {count > 0 && ` · ${actionsSummary(macro.actions, t)}`}
                  </p>
                </div>
                {editable && (
                  <span className="flex items-center gap-0.5 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 h-11 md:h-8"
                      onClick={() => openEdit(macro)}
                      aria-label={t("Editar macro {{nombre}}").replace("{{nombre}}", macro.name)}
                    >
                      <Pencil className="w-4 h-4" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 h-11 md:h-8 text-status-error hover:bg-status-error-soft"
                      onClick={() => setDeleteTarget(macro)}
                      aria-label={t("Eliminar macro {{nombre}}").replace("{{nombre}}", macro.name)}
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <MacroFormModal
        open={formOpen}
        mode={editingId ? "edit" : "create"}
        draft={draft}
        onChange={(next) => {
          setDraft(next);
          if (serverError) setServerError(null);
        }}
        onSubmit={handleSubmit}
        onClose={() => setFormOpen(false)}
        saving={saving}
        serverError={serverError}
        canGlobal={isSupervisor}
      />

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        size="sm"
        title={t("Eliminar macro")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("Cancelar")}
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleting}>
              {t("Eliminar")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg">{t("Vas a eliminar esta macro:")}</p>
        <p className="mt-1 text-sm font-semibold text-fg break-words">{deleteTarget?.name}</p>
        <p className="mt-3 text-sm text-fg-muted">
          {deleteTarget?.visibility === "global"
            ? t("Deja de estar disponible para todo el equipo. No se puede deshacer.")
            : t("Deja de estar disponible en tus chats. No se puede deshacer.")}
        </p>
      </Modal>
    </div>
  );
}
